import { useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { useNavigate } from 'react-router-dom';

import { supabase } from '@maestra/core/lib/supabase';
import { useAppDispatch, useAppSelector } from '@maestra/core/store/store';
import { artistsActions } from '@maestra/core/store/slices/artists';
import { perfilEstaAtivo } from '@maestra/core/hooks/useArtistCapabilities';
import { useEntitlements } from '@maestra/core/hooks/useEntitlements';
import { shouldEnrichChartmetric } from '@maestra/core/lib/chartmetricFreshness';

// O CÓDIGO DE ACESSO (Pass Access) NUM MODAL (pedido do Lucas, 13/10/2026). Antes, "Tenho um código
// de acesso" levava ao checkout com o campo em foco, e quem era novo se perdia entre o código e o
// pagamento. Agora qualquer tela abre o mesmo modal: o código, e no sucesso o caminho para criar o
// planejamento. O campo da tela do desbloqueio continua, e o checkout ganha a porta para o modal.
//
// ⚠️ O CÓDIGO LIBERA O PLANEJAMENTO (e o plano) DE UM PERFIL, e não o perfil inteiro nem a conta
// (`liberado_por_codigo`, 27/09/2026). Por isso ele pede um perfil: o da tela, ou, aberto de fora de
// um perfil (o checkout da /planos), a escolha entre os perfis do dono que ainda não estão liberados.

// ── Abrir de qualquer lugar: um modal só, montado na raiz do app ──
type Estado = { aberto: boolean; artistId: string | null };
let estado: Estado = { aberto: false, artistId: null };
const ouvintes = new Set<() => void>();
const mudar = (novo: Estado) => { estado = novo; ouvintes.forEach((o) => o()); };

/** Abre o modal do código. Sem perfil, o modal pergunta para qual. */
export const abrirCodigoDeAcesso = (artistId?: string | null) => mudar({ aberto: true, artistId: artistId ?? null });
export const fecharCodigoDeAcesso = () => mudar({ ...estado, aberto: false });

const useEstadoDoCodigo = () => useSyncExternalStore(
  (o) => { ouvintes.add(o); return () => { ouvintes.delete(o); }; },
  () => estado,
);

export const useCodigoDeAcesso = () => {
  const { aberto, artistId: pedido } = useEstadoDoCodigo();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const user = useAppSelector((s) => s.auth.user);
  const artistas = useAppSelector((s) => s.artists.items);
  const { isPro } = useEntitlements();

  // Os perfis do dono que o código ainda pode liberar.
  const elegiveis = useMemo(() => artistas.filter((a) => a.role !== 'member' && !a.liberado_por_codigo && !perfilEstaAtivo(a, isPro)), [artistas, isPro]);

  const [escolhido, setEscolhido] = useState<string | null>(null);
  const [codigo, setCodigo] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [resgatando, setResgatando] = useState(false);
  const [liberado, setLiberado] = useState<{ id: string; nome: string } | null>(null);

  // Cada abertura começa do zero, já com o perfil da tela (ou o único que dá para liberar).
  useEffect(() => {
    if (!aberto) return;
    setCodigo(''); setErro(null); setLiberado(null); setResgatando(false);
    setEscolhido(pedido ?? (elegiveis.length === 1 ? elegiveis[0].id : null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberto, pedido]);

  const perfil = artistas.find((a) => a.id === escolhido) ?? null;

  const usar = async () => {
    const code = codigo.trim();
    if (!code) { setErro('Digite o código.'); return; }
    if (!perfil) { setErro('Escolha o perfil que o código vai liberar.'); return; }
    setResgatando(true);
    setErro(null);
    try {
      const { data } = await supabase.functions.invoke('redeem-access-pass', { body: { code, artistId: perfil.id } });
      if (!data?.ok) { setErro(data?.error || 'Código inválido ou já utilizado.'); return; }
      // A lista relida traz a marca `liberado_por_codigo`, que abre o planejamento.
      if (user?.id) await dispatch(artistsActions.fetchArtists(user.id));
      if (shouldEnrichChartmetric(perfil.content?.chartmetricProfile)) {
        supabase.functions.invoke('artist-enrich-chartmetric', { body: { artistId: perfil.id } }).catch(() => {});
      }
      setLiberado({ id: perfil.id, nome: perfil.name });
    } catch {
      setErro('Não foi possível validar o código. Confira a conexão e tente de novo.');
    } finally {
      setResgatando(false);
    }
  };

  return {
    aberto,
    fechar: fecharCodigoDeAcesso,
    /** Aberto de fora de um perfil e com mais de um para liberar: a pessoa escolhe. */
    perfis: !pedido && elegiveis.length > 1 ? elegiveis.map((a) => ({ id: a.id, nome: a.name })) : null,
    /** Aberto de fora de um perfil e sem nenhum para liberar. */
    semPerfil: !pedido && elegiveis.length === 0,
    escolhido,
    escolher: (id: string | null) => { setEscolhido(id); setErro(null); },
    nomeDoPerfil: perfil?.name ?? null,
    codigo,
    mudarCodigo: (v: string) => { setCodigo(v.toUpperCase()); setErro(null); },
    usar: () => { void usar(); },
    resgatando,
    erro,
    liberado: liberado ? {
      titulo: 'Planejamento liberado',
      texto: `O código liberou o planejamento estratégico de ${liberado.nome}. A Nyta conduz a conversa, passo a passo, a partir do diagnóstico.`,
      criar: () => { fecharCodigoDeAcesso(); navigate(`/artists/${liberado.id}/wizard`); },
    } : null,
  };
};

export type DadosDoCodigoDeAcesso = ReturnType<typeof useCodigoDeAcesso>;
